import AppVersion from '../components/AppVersion';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  LogOut,
  Settings,
  X,
  Phone,
  MapPin,
  Plus,
  LayoutDashboard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useToast } from '../hooks/use-toast';
import * as crmBackend from '../services/crmBackendService';
import { useLocationTracking } from '../hooks/useLocationTracking';
import { getWeekRange, isJobInactiveForTechList, isThisWeekActiveJob } from '../lib/techJobFilters';

const TechDashboard = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [filter, setFilter] = useState('today');
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ todayJobs: 0, activeJobs: 0, completedToday: 0, weeklyJobs: 0 });

  const { toast } = useToast();
  const technicianName = localStorage.getItem('technicianName') || '';
  const userRole = localStorage.getItem('userRole') || 'tech';
  const isTech = userRole === 'tech';
  const techUser = JSON.parse(localStorage.getItem('techUser') || '{}');
  // Tech identity comes from the tech-login localStorage keys above -- but those
  // keys are tech-portal-only and are never cleared by an admin login, so an
  // admin session (e.g. John viewing the Tech Portal via the new sidebar link)
  // used to silently inherit whatever tech last logged in on this browser
  // ("Technician 1") instead of showing his own identity. For an admin session,
  // decode the real username straight out of the live authToken instead --
  // the same source Dashboard.jsx's getCurrentUser() already uses -- so it can
  // never go stale/cross-contaminate between roles.
  const adminDisplayName = (() => {
    const token = localStorage.getItem('authToken');
    if (!token) return 'Admin';
    try {
      // authToken is base64("<username>:<uuid>") (see tech-backend/server.py
      // auth_login), a plain colon-delimited string -- NOT JSON. (Dashboard.jsx's
      // getCurrentUser() tries JSON.parse on this same value; it always throws
      // and silently falls back to the generic "Admin" string -- harmless there
      // since the "your identity" bug being fixed here is only on this page.)
      const decoded = atob(token).split(':')[0];
      return decoded || 'Admin';
    } catch {
      return 'Admin';
    }
  })();
  const displayName = isTech ? (technicianName || techUser.name || 'Tech') : adminDisplayName;

  useLocationTracking(technicianName, '', true);

  // Job list is a to-do list of current/upcoming work, not a history browser --
  // scope the fetch itself to the current week (Sun-Sat containing today, matching
  // TechSchedule.jsx's convention) instead of the old 1-month-back/2-months-forward
  // window. That old window meant every tab -- including the status-only ones
  // (Scheduled/En Route/In Progress/Completed) -- searched ~3 months of jobs with no
  // date restriction. See lib/techJobFilters.js for the shared filter logic and why
  // custom_pipeline_status (not just custom_job_status) has to factor into "done".
  const loadJobs = async () => {
    const { startStr, endStr } = getWeekRange();
    try {
      const result = await crmBackend.getScheduledQuotations(
        startStr, endStr, isTech ? (techUser.email || null) : null
      );
      const data = result.data || [];
      setJobs(data);
      const today = new Date().toISOString().split('T')[0];
      setStats({
        todayJobs: data.filter(j => j.custom_scheduled_date === today).length,
        activeJobs: data.filter(j => crmBackend.normalizeJobStatus(j.custom_job_status) === 'In Progress').length,
        completedToday: data.filter(j => crmBackend.normalizeJobStatus(j.custom_job_status) === 'Completed' && j.custom_scheduled_date === today).length,
        weeklyJobs: data.filter(j => isThisWeekActiveJob(j, startStr, endStr)).length,
      });
    } catch (err) {
      toast({ title: 'Could not load jobs', description: err.message, variant: 'destructive' });
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('techAuthenticated');
    localStorage.removeItem('techUser');
    if (!isTech) {
      // Viewing as an admin session (e.g. John) -- clear that session too so
      // Logout here actually logs out, instead of leaving a live admin
      // session while dropping them on the tech login page.
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('authToken');
      navigate('/');
      return;
    }
    navigate('/tech');
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'text-red-600',
      'Medium': 'text-amber-600',
      'Low': 'text-gray-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  const today = new Date().toISOString().split('T')[0];

  // 'today' and 'week' are the to-do views -- a job already closed out (office paid/
  // invoiced/cancelled/declined it, or the tech marked it Completed/Cancelled) has
  // nothing left to act on, so it's excluded regardless of its scheduled date. The
  // status tabs (Scheduled/En Route/In Progress/Completed) are an explicit request
  // for that status, so they aren't re-filtered by isJobInactiveForTechList -- but
  // `jobs` itself is already scoped to the current week by loadJobs(), so those tabs
  // no longer reach back into old weeks either.
  const filteredJobs = filter === 'today'
    ? jobs.filter(j => j.custom_scheduled_date === today && !isJobInactiveForTechList(j))
    : filter === 'week'
    ? (() => {
        const { startStr, endStr } = getWeekRange();
        return jobs.filter(j => isThisWeekActiveJob(j, startStr, endStr));
      })()
    : jobs.filter(j => crmBackend.normalizeJobStatus(j.custom_job_status) === filter);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-green-700 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                <Wrench className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <h1 className="font-bold text-lg">AAA IRRIGATION</h1>
                <p className="text-xs text-green-100">SERVICE, LLC</p>
              </div>
            </div>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-green-600 rounded-lg transition-colors"
              aria-label="Settings"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Settings className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Slide-down Menu */}
        {menuOpen && (
          <div className="bg-green-600 border-t border-green-500 animate-in slide-in-from-top-5">
            <div className="px-4 py-4 space-y-4">
              <div className="flex items-center space-x-3 pb-4 border-b border-green-500">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-white text-green-700 font-bold">
                    {displayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{displayName}</p>
                  <p className="text-sm text-green-100">{userRole}</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/tech/schedule')}
                className="w-full flex items-center space-x-3 p-3 hover:bg-green-500 rounded-lg transition-colors"
              >
                <Calendar className="w-5 h-5" />
                <span>Weekly Schedule</span>
              </button>
              {!isTech && (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center space-x-3 p-3 hover:bg-green-500 rounded-lg transition-colors"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>Admin Portal</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 p-3 hover:bg-green-500 rounded-lg transition-colors text-green-100"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))]">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Today's Jobs</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.todayJobs}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Now</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{stats.activeJobs}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.completedToday}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">This Week</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.weeklyJobs}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-2 mb-4 overflow-x-auto pb-2">
          {['today', 'week', 'Scheduled', 'En Route', 'In Progress', 'Completed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {f === 'today' ? "Today's Jobs" : f === 'week' ? 'This Week' : f}
            </button>
          ))}
        </div>

        {/* Jobs List */}
        <div className="space-y-3">
          {filteredJobs.map((job) => (
            <Card
              key={job.name}
              className="bg-white hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/tech/job/${job.name}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="font-bold text-gray-900 text-lg">{job.party_name}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{job.name}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>

                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{job.custom_scheduled_time}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">
                      {job.custom_service_address
                        ? job.custom_service_address.split(',').slice(1).join(',').trim() || job.custom_service_address
                        : ''}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Badge className={(job.custom_pipeline_status
                    ? crmBackend.pipelineStatusColor(job.custom_pipeline_status)
                    : crmBackend.getJobStatusColor(crmBackend.displayStatus(job.custom_job_status, job.custom_approval_status))) + ' border'}>
                    {crmBackend.pipelineStatusLabel(job)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg pb-[env(safe-area-inset-bottom,0px)]">
        <div className="grid grid-cols-3 gap-1 p-2">
          <button
            onClick={() => navigate('/tech/dashboard')}
            className="flex flex-col items-center space-y-1 py-2 px-4 rounded-lg bg-green-50 text-green-600"
          >
            <Calendar className="w-6 h-6" />
            <span className="text-xs font-medium">Jobs</span>
          </button>
          <button
            onClick={() => navigate('/tech/create-job')}
            className="flex flex-col items-center space-y-1 py-2 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <Plus className="w-6 h-6" />
            <span className="text-xs font-medium">New Job</span>
          </button>
          <button
            onClick={() => navigate('/tech/schedule')}
            className="flex flex-col items-center space-y-1 py-2 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <Clock className="w-6 h-6" />
            <span className="text-xs font-medium">Schedule</span>
          </button>
        </div>
      </div>
      <div className="pb-24"><AppVersion /></div>
    </div>
  );
};

export default TechDashboard;
